import { useNavigate } from 'react-router-dom';
import { useLocale } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useEPeticeDetailContext } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	ContentLayout,
	ErrorStatusBanner,
} from '@gov-nx/ui/web';
import { pageRouteResolver } from '@gov-nx/web';
import { CmsPageRender } from '../../../Cms/CmsPageRender';
import { EPetitionChips } from '../components/EPetitionChips';
import { EPetitionAttachments } from './EPetitionAttachments';
import { EPetitionDetailSkeleton } from './EPetitionDetailSkeleton';
import { EPetitionExtend } from './EPetitionExtend';
import { EPetitionInfo } from './EPetitionInfo';
import { EPetitionPerex } from './EPetitionPerex';
import { EPetitionShareMessage } from './EPetitionShareMessage';
import { EPetitionSign } from './EPetitionSign';
import { EPetitionText } from './EPetitionText';
import { EPetitionCloseAndSubmit } from './ePetitionCloseAndSubmit';

export interface EPetitionDetailViewProps {
	code: PageCode;
}

export const EPetitionDetailView = ({ code }: EPetitionDetailViewProps) => {
	const {
		petition,
		individualPerson,
		ePetitionSign,
		ePetitionExtend,
		controls,
		isEditable,
	} = useEPeticeDetailContext();
	const { t } = useLocale(code);
	const navigate = useNavigate();

	const getContent = () => {
		if (controls.initialLoading) {
			return <EPetitionDetailSkeleton />;
		}

		if (controls.initialError) {
			return <ErrorStatusBanner code={code} />;
		}

		return (
			<>
				{petition ? (
					<EPetitionChips
						code={code}
						petition={petition}
						size="m"
					/>
				) : null}
				<EPetitionCloseAndSubmit code={code} />
				{ePetitionExtend.canBeExtended && <EPetitionExtend code={code} />}

				<ContentLayout>
					<h2 className="text-headline-s!">
						<EPetitionPerex perex={petition?.perex} />
					</h2>
					<EPetitionText
						text={petition?.text}
						strings={{
							showMore: t('akce.zobrazit-vice'),
							showLess: t('akce.zobrazit-mene'),
						}}
					/>
					<section>
						<EPetitionAttachments code={code} />
					</section>
					<section>
						<EPetitionInfo code={code} />
					</section>
					{isEditable && (
						<ButtonGroupLayout>
							<Button
								color="primary"
								type="solid"
								onClick={() =>
									navigate(
										pageRouteResolver('epetice.detail.editace', {
											petitionId: petition?.id,
										})
									)
								}
								aria-label={t('akce.wcag.editovat')}>
								{t('akce.editovat')}
							</Button>
						</ButtonGroupLayout>
					)}
				</ContentLayout>
				{individualPerson && ePetitionSign.isPublic && (
					<EPetitionSign code={code} />
				)}

				{petition?.datumZverejneni && (
					<EPetitionShareMessage
						petition={petition}
						code={code}
					/>
				)}
			</>
		);
	};

	return (
		<CmsPageRender
			code={code}
			override={{ name: petition?.nazev }}
			showDescription={false}>
			{getContent()}
		</CmsPageRender>
	);
};
